import { ChangeDetectorRef, Component, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { FirebaseAuthService } from '../../../../core/firebase/firebase-auth.service';

type LoginForm = FormGroup<{
    email: FormControl<string>;
    password: FormControl<string>;
}>;

@Component({
    selector: 'app-login-page',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    styles: [
        `
            .wrap {
                min-height: 100vh;
                display: grid;
                place-items: center;
                background: #f6f7fb;
                padding: 24px;
            }
            .card {
                width: 100%;
                max-width: 420px;
                background: #fff;
                border: 1px solid #e9e9ee;
                border-radius: 18px;
                padding: 18px;
                box-shadow: 0 10px 30px rgba(20, 20, 40, 0.06);
            }
            h1 {
                margin: 0 0 6px;
                font-size: 20px;
            }
            p {
                margin: 0 0 14px;
                color: #5a5a66;
                font-size: 13px;
            }
            label {
                font-size: 12px;
                color: #4b4b55;
            }
            .field {
                display: flex;
                flex-direction: column;
                gap: 6px;
                margin-bottom: 12px;
            }
            input {
                border: 1px solid #e5e5ea;
                border-radius: 12px;
                padding: 10px 12px;
                outline: none;
                font-size: 14px;
                background: #fff;
            }
            input:focus {
                border-color: #b9c7ff;
                box-shadow: 0 0 0 3px rgba(96, 124, 255, 0.15);
            }
            .actions {
                display: flex;
                gap: 10px;
                margin-top: 10px;
            }
            .btn {
                width: 100%;
                border: 1px solid #e5e5ea;
                background: #fff;
                padding: 10px 12px;
                border-radius: 12px;
                cursor: pointer;
                font-weight: 800;
            }
            .btn.primary {
                border-color: #c9d2ff;
                background: #eef2ff;
            }
            .btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
            .err {
                margin-top: 12px;
                padding: 12px;
                border: 1px solid #ffd0d0;
                background: #fff6f6;
                border-radius: 12px;
                font-size: 12px;
            }
            .small {
                margin-top: 10px;
                font-size: 12px;
                color: #6b6b76;
                text-align: center;
            }
        `,
    ],
    template: `
        <div class="wrap">
            <div class="card">
                <h1>Login</h1>
                <p>Ingresa tus credenciales para acceder al Builder.</p>

                <form [formGroup]="form" (ngSubmit)="submit()">
                    <div class="field">
                        <label>Email</label>
                        <input formControlName="email" placeholder="correo@dominio.com" />
                    </div>

                    <div class="field">
                        <label>Contraseña</label>
                        <input type="password" formControlName="password" placeholder="********" />
                    </div>

                    <div class="actions">
                        <button class="btn primary" type="submit" [disabled]="loading() || form.invalid">
                            {{ loading() ? 'Ingresando...' : 'Ingresar' }}
                        </button>
                    </div>
                </form>

                <div class="err" *ngIf="error()">{{ error() }}</div>

                <!-- ✅ usa el signal (reactivo) -->
                <div class="small" *ngIf="auth.userSig()?.email as em">
                    Sesión activa: <strong>{{ em }}</strong>
                </div>
            </div>
        </div>
    `,
})
export class LoginPage {
    loading = signal(false);
    error = signal('');

    form: LoginForm;

    constructor(
        private fb: FormBuilder,
        public auth: FirebaseAuthService,
        private router: Router,
        private cdr: ChangeDetectorRef
    ) {
        this.form = this.fb.group({
            email: this.fb.nonNullable.control('', [
                Validators.required,
                Validators.email,
            ]),
            password: this.fb.nonNullable.control('', [
                Validators.required,
                Validators.minLength(6),
            ]),
        });

        // ✅ si ya hay sesión (o cuando termine el login), navegar automáticamente
        effect(() => {
            const ready = this.auth.readySig();
            const user = this.auth.userSig();
            if (ready && user) {
                // usa la ruta real de tu home; dejo /home porque ya lo tenías
                this.router.navigateByUrl('/builder/content');
            }
        });
    }

    async submit(): Promise<void> {
        this.error.set('');
        if (this.form.invalid) return;

        this.loading.set(true);
        try {
            const { email, password } = this.form.getRawValue();
            await this.auth.login(email.trim(), password);

            // ✅ navegación inmediata (por si quieres que sea instantáneo)
            await this.router.navigateByUrl('/builder/content');
        } catch (e: any) {
            this.error.set(e?.message ?? 'No se pudo iniciar sesión.');
        } finally {
            this.loading.set(false);
            this.cdr.detectChanges();
        }
    }
}
